import {
  Button,
  breakpoints,
  Col,
  Icon,
  Input,
  Spinner,
  Text,
  type TextStyle,
  useHover,
  useTheme,
} from "@rally/ui-kit";
import { useState } from "react";
import { say } from "../localization/say.ts";

export type KeyRowProps = {
  label: string;

  hint: string;

  done: boolean;

  called: string;
  onCalled: (said: string) => void;
  onRemove: () => void;

  onAdd?: (() => void) | undefined;

  waiting?: boolean;

  ready?: boolean;

  spare?: boolean;

  wrong?: string | null;

  removable?: boolean;
  testID?: string;
};

export function KeyRow({
  label,
  hint,
  done,
  called,
  onCalled,
  onRemove,
  onAdd,
  waiting = false,
  ready = false,
  wrong = null,
  removable = true,
  testID,
}: KeyRowProps) {
  const theme = useTheme();
  const hover = useHover();
  const [editing, setEditing] = useState(false);
  return (
    <Col
      {...hover.hoverProps}
      testID={testID}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[3],
        paddingVertical: theme.space[3],
        minHeight: 64,
        borderBottomWidth: 1,
        borderBottomColor: wrong ? theme.color.danger : theme.color["border.default"],
        backgroundColor: "transparent",
        opacity: done || ready || waiting ? 1 : 0.55,
      }}
    >
      {waiting ? (
        <Spinner size="sm" />
      ) : (
        <Icon name="key" size={18} color={done ? "accent" : "text.tertiary"} />
      )}
      <Col style={{ flex: 1, gap: 2 }}>
        {editing ? (
          <Input
            value={called}
            onChangeText={onCalled}
            placeholder={label}
            placeholderTextColor={theme.color["text.tertiary"]}
            accessibilityLabel={say("join.callIt")(label)}
            autoFocus
            onSubmitEditing={() => setEditing(false)}
            testID={testID ? `${testID}-name` : undefined}
            style={
              {
                fontFamily: theme.font.sans,
                fontSize: theme.type.label.size,
                fontWeight: theme.type.label.weight,
                lineHeight: theme.type.label.line,
                letterSpacing: theme.type.label.letterSpacing,
                color: theme.color["text.primary"],
                padding: 0,
                borderWidth: 0,
                backgroundColor: "transparent",
                outlineStyle: "none",
              } as unknown as TextStyle
            }
          />
        ) : (
          <Text typeName="label">{called.trim() || label}</Text>
        )}
        <Text
          typeName="caption"
          color={wrong ? "danger" : done ? "text.secondary" : "text.tertiary"}
          accessibilityLiveRegion={wrong ? "polite" : "none"}
        >
          {wrong ?? hint}
        </Text>
      </Col>
      {done && (editing || hover.hovered || theme.isAtMost(breakpoints.phone)) ? (
        <Col style={{ flexDirection: "row", gap: theme.space[1] }}>
          {editing || !removable ? null : (
            <Button
              variant="ghost"
              icon="trash"
              accessibilityLabel={say("join.removeKey")}
              onPress={onRemove}
              testID={testID ? `${testID}-remove` : undefined}
            />
          )}
          <Button
            variant="ghost"
            icon={editing ? "check" : "pencil"}
            accessibilityLabel={editing ? say("join.keepName") : say("join.callIt")(label)}
            onPress={() => setEditing(!editing)}
            testID={testID ? `${testID}-rename` : undefined}
          />
        </Col>
      ) : onAdd ? (
        <Button
          variant={ready ? "primary" : "ghost"}
          disabled={!ready || waiting}
          loading={waiting}
          onPress={onAdd}
          testID={testID ? `${testID}-add` : undefined}
        >
          {say("join.add")}
        </Button>
      ) : null}
    </Col>
  );
}
